import * as THREE from 'three';
import {
  createCoinPusherMachine,
  disposeCoinPusherMachine,
  getCoinPusherMachineRuntime,
  setCoinPusherPhase,
  type CoinPusherDetailLevel,
  type CoinPusherMachine,
  type CoinPusherMachineOptions,
  type CoinPusherMachineRuntime,
} from '../assets/machine';

export type ArcadeSceneQuality = 'full' | 'performance';

export type ArcadeSceneOptions = {
  renderer?: THREE.WebGLRenderer;
  quality?: ArcadeSceneQuality;
  seed?: number;
  origin?: THREE.Vector3 | readonly [number, number, number];
  machineRotationY?: number;
  machineOptions?: CoinPusherMachineOptions;
  configureScene?: boolean;
  addLighting?: boolean;
  environmentMap?: boolean;
  animateMachinePreview?: boolean;
  background?: THREE.ColorRepresentation;
  fogColor?: THREE.ColorRepresentation;
};

export type ArcadeSceneDiagnostics = {
  meshes: number;
  instancedMeshes: number;
  instances: number;
  geometries: number;
  materials: number;
  textures: number;
  triangles: number;
  approximateDrawSurfaces: number;
};

export type ArcadeSceneMetadata = {
  readonly version: 1;
  readonly assetKind: 'premium-arcade-scene';
  readonly quality: ArcadeSceneQuality;
  readonly coordinateFrame: {
    readonly front: '+Z';
    readonly up: '+Y';
    readonly origin: 'machine-footprint-center';
  };
  readonly artDirection: string;
  readonly signage: readonly string[];
  readonly layerNames: readonly string[];
  readonly machineFloorY: number;
  readonly sceneBounds: THREE.Box3;
  readonly gameplayViewBounds: THREE.Box3;
  readonly initialDiagnostics: ArcadeSceneDiagnostics;
  readonly reflectionSource: 'pmrem-arcade-capture' | 'procedural-equirect' | 'preserved';
};

export type ArcadeSceneResult = {
  readonly root: THREE.Group;
  readonly machine: CoinPusherMachine;
  readonly machineRuntime: CoinPusherMachineRuntime;
  readonly layers: Readonly<{
    ground: THREE.Group;
    foreground: THREE.Group;
    midground: THREE.Group;
    far: THREE.Group;
    lighting: THREE.Group;
    sockets: THREE.Group;
  }>;
  readonly sockets: Readonly<Record<string, THREE.Object3D>>;
  readonly bounds: THREE.Box3;
  readonly gameplayViewBounds: THREE.Box3;
  readonly metadata: ArcadeSceneMetadata;
  update(elapsedSeconds: number, deltaSeconds: number): void;
  setGameplayInteriorVisible(visible: boolean): void;
  getDiagnostics(): ArcadeSceneDiagnostics;
  dispose(): void;
};

type SceneMaterials = {
  midnight: THREE.MeshStandardMaterial;
  silhouette: THREE.MeshStandardMaterial;
  podium: THREE.MeshPhysicalMaterial;
  podiumEdge: THREE.MeshPhysicalMaterial;
  brass: THREE.MeshPhysicalMaterial;
  chrome: THREE.MeshPhysicalMaterial;
  enamelPink: THREE.MeshPhysicalMaterial;
  enamelCyan: THREE.MeshPhysicalMaterial;
  fabricPink: THREE.MeshStandardMaterial;
  fabricCyan: THREE.MeshStandardMaterial;
  awningPink: THREE.MeshStandardMaterial;
  awningCyan: THREE.MeshStandardMaterial;
  glass: THREE.MeshPhysicalMaterial;
  bulbWarm: THREE.MeshStandardMaterial;
  bulbPink: THREE.MeshStandardMaterial;
  bulbCyan: THREE.MeshStandardMaterial;
  neonPink: THREE.MeshStandardMaterial;
  neonCyan: THREE.MeshStandardMaterial;
  neonWarm: THREE.MeshStandardMaterial;
  backdrop: THREE.MeshBasicMaterial;
};

type GeometryKit = {
  unitBox: THREE.BoxGeometry;
  unitPlane: THREE.PlaneGeometry;
  unitCylinder: THREE.CylinderGeometry;
  bulb: THREE.SphereGeometry;
};

type SignAnimation = {
  object: THREE.Object3D;
  material: THREE.MeshStandardMaterial;
  baseY: number;
  phase: number;
  amplitude: number;
};

type FerrisAnimation = {
  wheel: THREE.Group;
  gondolas: THREE.Group[];
};

type AnimationState = {
  signs: SignAnimation[];
  ferris: FerrisAnimation | null;
  bokeh: THREE.Points[];
  practicalLights: THREE.Light[];
};

type BuildContext = {
  resources: OwnedResources;
  materials: SceneMaterials;
  geometry: GeometryKit;
  quality: ArcadeSceneQuality;
  random: () => number;
  animation: AnimationState;
};

const MACHINE_FLOOR_Y = 0.38;
const Y_AXIS = new THREE.Vector3(0, 1, 0);
const SIGNAGE = ['LUCKY TIDE', 'PRIZE PIER', 'BONUS BAY', 'PLAY THE TIDE'] as const;

export function createArcadeScene(scene: THREE.Scene, options?: ArcadeSceneOptions): ArcadeSceneResult;
export function createArcadeScene(
  scene: THREE.Scene,
  renderer: THREE.WebGLRenderer,
  options?: Omit<ArcadeSceneOptions, 'renderer'>,
): ArcadeSceneResult;
export function createArcadeScene(
  scene: THREE.Scene,
  rendererOrOptions: THREE.WebGLRenderer | ArcadeSceneOptions = {},
  trailingOptions: Omit<ArcadeSceneOptions, 'renderer'> = {},
): ArcadeSceneResult {
  const options: ArcadeSceneOptions = isWebGLRenderer(rendererOrOptions)
    ? { ...trailingOptions, renderer: rendererOrOptions }
    : rendererOrOptions;
  const quality = options.quality ?? 'full';
  const resources = new OwnedResources();
  const root = new THREE.Group();
  root.name = 'arcadeScene.root';
  copyVectorLike(root.position, options.origin ?? [0, 0, 0]);

  const ground = createLayer(root, 'arcadeScene.layer.ground');
  const foreground = createLayer(root, 'arcadeScene.layer.foreground');
  const midground = createLayer(root, 'arcadeScene.layer.midground');
  const far = createLayer(root, 'arcadeScene.layer.far');
  const lighting = createLayer(root, 'arcadeScene.layer.lighting');
  const socketLayer = createLayer(root, 'arcadeScene.layer.sockets');

  const materials = createMaterials(resources, options.renderer);
  const geometry = createGeometryKit(resources, quality);
  const animation: AnimationState = {
    signs: [],
    ferris: null,
    bokeh: [],
    practicalLights: [],
  };
  const context: BuildContext = {
    resources,
    materials,
    geometry,
    quality,
    random: createRandom(options.seed ?? 0xa7c4d3),
    animation,
  };

  buildGround(context, ground);
  buildForeground(context, foreground);
  buildMidground(context, midground);
  buildFarLayer(context, far);
  if (options.addLighting !== false) buildLighting(context, lighting, quality);

  const machineDetail: CoinPusherDetailLevel = quality === 'performance' ? 'performance' : 'full';
  const machine = createCoinPusherMachine({
    detailLevel: machineDetail,
    coinCountScale: quality === 'performance' ? 0.68 : 1,
    ...options.machineOptions,
  });
  machine.name = 'arcadeScene.hero.coinPusher';
  machine.position.y = MACHINE_FLOOR_Y;
  machine.rotation.y = options.machineRotationY ?? 0;
  root.add(machine);
  const machineRuntime = getCoinPusherMachineRuntime(machine);

  const sockets = buildSceneSockets(socketLayer, machineRuntime);
  const bounds = new THREE.Box3(
    new THREE.Vector3(-13.5, -0.05, -17.5),
    new THREE.Vector3(13.5, 10.5, 8.5),
  );
  const gameplayViewBounds = new THREE.Box3(
    new THREE.Vector3(-2.8, 1.72 + MACHINE_FLOOR_Y, -1.45),
    new THREE.Vector3(2.8, 4.75 + MACHINE_FLOOR_Y, 2.2),
  );

  const previousBackground = scene.background;
  const previousFog = scene.fog;
  const previousEnvironment = scene.environment;
  let appliedBackground: THREE.Color | null = null;
  let appliedFog: THREE.Fog | null = null;

  if (options.configureScene !== false) {
    appliedBackground = new THREE.Color(options.background ?? '#080611');
    appliedFog = new THREE.Fog(options.fogColor ?? '#10091d', 16, 40);
    scene.background = appliedBackground;
    scene.fog = appliedFog;
  }

  let environmentTarget: THREE.WebGLRenderTarget | null = null;
  let appliedEnvironment: THREE.Texture | null = null;
  let reflectionSource: ArcadeSceneMetadata['reflectionSource'] = 'preserved';
  if (options.environmentMap !== false) {
    if (options.renderer) {
      const target = createArcadeEnvironmentMap(options.renderer);
      environmentTarget = target;
      appliedEnvironment = target.texture;
      reflectionSource = 'pmrem-arcade-capture';
    } else {
      appliedEnvironment = resources.texture(createProceduralEnvironmentTexture());
      reflectionSource = 'procedural-equirect';
    }
    scene.environment = appliedEnvironment;
  }

  scene.add(root);

  let disposed = false;
  let gameplayInteriorVisible = true;
  const animateMachinePreview = options.animateMachinePreview !== false;
  const decorativeInterior = collectDecorativeInterior(machineRuntime);

  const getDiagnostics = (): ArcadeSceneDiagnostics => collectDiagnostics(root);
  const initialDiagnostics = getDiagnostics();
  const metadata: ArcadeSceneMetadata = {
    version: 1,
    assetKind: 'premium-arcade-scene',
    quality,
    coordinateFrame: {
      front: '+Z',
      up: '+Y',
      origin: 'machine-footprint-center',
    },
    artDirection: 'Midnight resort boardwalk: flamingo pink, lagoon cyan, brass, lacquer, and warm carnival bulbs.',
    signage: SIGNAGE,
    layerNames: [ground.name, foreground.name, midground.name, far.name, lighting.name, socketLayer.name],
    machineFloorY: MACHINE_FLOOR_Y,
    sceneBounds: bounds.clone(),
    gameplayViewBounds: gameplayViewBounds.clone(),
    initialDiagnostics,
    reflectionSource,
  };
  root.userData.arcadeScene = metadata;

  const setGameplayInteriorVisible = (visible: boolean): void => {
    gameplayInteriorVisible = visible;
    for (const object of decorativeInterior) object.visible = visible;
  };

  const update = (elapsedSeconds: number, deltaSeconds: number): void => {
    if (disposed) return;
    const elapsed = Number.isFinite(elapsedSeconds) ? elapsedSeconds : 0;
    const delta = THREE.MathUtils.clamp(Number.isFinite(deltaSeconds) ? deltaSeconds : 0, 0, 0.1);

    materials.bulbWarm.emissiveIntensity = 2.15 + Math.sin(elapsed * 3.1) * 0.26;
    materials.bulbPink.emissiveIntensity = 1.82 + Math.sin(elapsed * 3.7 + 1.8) * 0.3;
    materials.bulbCyan.emissiveIntensity = 1.62 + Math.sin(elapsed * 2.9 + 3.4) * 0.24;
    materials.neonPink.emissiveIntensity = 2.15 + Math.sin(elapsed * 1.7) * 0.18;
    materials.neonCyan.emissiveIntensity = 1.95 + Math.sin(elapsed * 1.45 + 2.1) * 0.16;

    animation.signs.forEach((entry, index) => {
      entry.object.position.y = entry.baseY + Math.sin(elapsed * 0.62 + entry.phase) * entry.amplitude;
      entry.object.rotation.z = Math.sin(elapsed * 0.43 + entry.phase) * 0.004;
      entry.material.emissiveIntensity = 0.42 + Math.sin(elapsed * 1.9 + index * 1.3) * 0.08;
    });

    if (animation.ferris) {
      animation.ferris.wheel.rotation.z += delta * 0.035;
      for (const gondola of animation.ferris.gondolas) gondola.rotation.z = -animation.ferris.wheel.rotation.z;
    }

    animation.bokeh.forEach((points, index) => {
      points.position.y = Math.sin(elapsed * (0.09 + index * 0.018) + index) * 0.12;
      points.rotation.z = Math.sin(elapsed * 0.035 + index) * 0.008;
    });

    animation.practicalLights.forEach((light, index) => {
      light.intensity = 9.5 + Math.sin(elapsed * 1.15 + index * 2.2) * 1.2;
    });

    if (animateMachinePreview && gameplayInteriorVisible) {
      setCoinPusherPhase(machine, 0.5 + Math.sin(elapsed * 1.05) * 0.5);
    }

    const machineWarm = machineRuntime.materials['bulb-warm'];
    const machinePink = machineRuntime.materials['bulb-pink'];
    const machineCyan = machineRuntime.materials['bulb-cyan'];
    if (machineWarm instanceof THREE.MeshStandardMaterial) {
      machineWarm.emissiveIntensity = 2.2 + Math.sin(elapsed * 3.3) * 0.24;
    }
    if (machinePink instanceof THREE.MeshStandardMaterial) {
      machinePink.emissiveIntensity = 1.98 + Math.sin(elapsed * 3.8 + 1.3) * 0.22;
    }
    if (machineCyan instanceof THREE.MeshStandardMaterial) {
      machineCyan.emissiveIntensity = 1.74 + Math.sin(elapsed * 3 + 2.7) * 0.2;
    }
  };

  const dispose = (): void => {
    if (disposed) return;
    disposed = true;
    root.removeFromParent();
    disposeCoinPusherMachine(machine);
    resources.dispose();
    environmentTarget?.dispose();
    if (scene.background === appliedBackground) scene.background = previousBackground;
    if (scene.fog === appliedFog) scene.fog = previousFog;
    if (scene.environment === appliedEnvironment) scene.environment = previousEnvironment;
  };

  return {
    root,
    machine,
    machineRuntime,
    layers: { ground, foreground, midground, far, lighting, sockets: socketLayer },
    sockets,
    bounds,
    gameplayViewBounds,
    metadata,
    update,
    setGameplayInteriorVisible,
    getDiagnostics,
    dispose,
  };
}

class OwnedResources {
  private readonly geometries = new Set<THREE.BufferGeometry>();
  private readonly materials = new Set<THREE.Material>();
  private readonly textures = new Set<THREE.Texture>();
  private disposed = false;

  geometry<T extends THREE.BufferGeometry>(geometry: T): T {
    this.geometries.add(geometry);
    return geometry;
  }

  material<T extends THREE.Material>(material: T): T {
    this.materials.add(material);
    return material;
  }

  texture<T extends THREE.Texture>(texture: T): T {
    this.textures.add(texture);
    return texture;
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    for (const geometry of this.geometries) geometry.dispose();
    for (const material of this.materials) material.dispose();
    for (const texture of this.textures) texture.dispose();
    this.geometries.clear();
    this.materials.clear();
    this.textures.clear();
  }
}

function createLayer(parent: THREE.Group, name: string): THREE.Group {
  const layer = new THREE.Group();
  layer.name = name;
  parent.add(layer);
  return layer;
}

function createGeometryKit(resources: OwnedResources, quality: ArcadeSceneQuality): GeometryKit {
  const radialSegments = quality === 'full' ? 14 : 10;
  return {
    unitBox: resources.geometry(new THREE.BoxGeometry(1, 1, 1)),
    unitPlane: resources.geometry(new THREE.PlaneGeometry(1, 1)),
    unitCylinder: resources.geometry(new THREE.CylinderGeometry(1, 1, 1, radialSegments)),
    bulb: resources.geometry(new THREE.SphereGeometry(1, quality === 'full' ? 10 : 7, quality === 'full' ? 7 : 5)),
  };
}

function createMaterials(resources: OwnedResources, renderer?: THREE.WebGLRenderer): SceneMaterials {
  const maxAnisotropy = Math.min(8, renderer?.capabilities.getMaxAnisotropy() ?? 4);
  const floorTexture = resources.texture(createFloorTexture(maxAnisotropy));
  const awningPinkTexture = resources.texture(createAwningTexture('#f42588', '#67104f', maxAnisotropy));
  const awningCyanTexture = resources.texture(createAwningTexture('#15c8d6', '#123a5f', maxAnisotropy));
  const backdropTexture = resources.texture(createBackdropTexture(maxAnisotropy));

  const midnight = resources.material(
    new THREE.MeshStandardMaterial({ color: '#0e0b18', roughness: 0.82, metalness: 0.2 }),
  );
  midnight.name = 'arcade.material.midnight';
  const silhouette = resources.material(
    new THREE.MeshStandardMaterial({ color: '#120b22', roughness: 0.92, metalness: 0.08 }),
  );
  silhouette.name = 'arcade.material.silhouette';
  const podium = resources.material(
    new THREE.MeshPhysicalMaterial({
      color: '#22132b',
      map: floorTexture,
      roughness: 0.38,
      metalness: 0.24,
      clearcoat: 0.36,
      clearcoatRoughness: 0.26,
      envMapIntensity: 0.85,
    }),
  );
  podium.name = 'arcade.material.podium';
  const podiumEdge = resources.material(
    new THREE.MeshPhysicalMaterial({
      color: '#3d1739',
      roughness: 0.26,
      metalness: 0.62,
      clearcoat: 0.45,
      clearcoatRoughness: 0.18,
      envMapIntensity: 1.05,
    }),
  );
  podiumEdge.name = 'arcade.material.podiumEdge';
  const brass = resources.material(
    new THREE.MeshPhysicalMaterial({
      color: '#e2a83c',
      roughness: 0.22,
      metalness: 0.95,
      clearcoat: 0.22,
      clearcoatRoughness: 0.12,
      envMapIntensity: 1.35,
    }),
  );
  brass.name = 'arcade.material.brass';
  const chrome = resources.material(
    new THREE.MeshPhysicalMaterial({
      color: '#cfe1e7',
      roughness: 0.16,
      metalness: 1,
      clearcoat: 0.35,
      clearcoatRoughness: 0.08,
      envMapIntensity: 1.5,
    }),
  );
  chrome.name = 'arcade.material.chrome';
  const enamelPink = resources.material(
    new THREE.MeshPhysicalMaterial({
      color: '#b71968',
      roughness: 0.3,
      metalness: 0.42,
      clearcoat: 0.7,
      clearcoatRoughness: 0.16,
    }),
  );
  enamelPink.name = 'arcade.material.enamelPink';
  const enamelCyan = resources.material(
    new THREE.MeshPhysicalMaterial({
      color: '#087c91',
      roughness: 0.3,
      metalness: 0.42,
      clearcoat: 0.68,
      clearcoatRoughness: 0.16,
    }),
  );
  enamelCyan.name = 'arcade.material.enamelCyan';
  const fabricPink = resources.material(
    new THREE.MeshStandardMaterial({ color: '#7e174d', roughness: 0.84, metalness: 0.02 }),
  );
  fabricPink.name = 'arcade.material.fabricPink';
  const fabricCyan = resources.material(
    new THREE.MeshStandardMaterial({ color: '#145269', roughness: 0.84, metalness: 0.02 }),
  );
  fabricCyan.name = 'arcade.material.fabricCyan';
  const awningPink = resources.material(
    new THREE.MeshStandardMaterial({ color: '#ffffff', map: awningPinkTexture, roughness: 0.76, metalness: 0.01 }),
  );
  awningPink.name = 'arcade.material.awningPink';
  const awningCyan = resources.material(
    new THREE.MeshStandardMaterial({ color: '#ffffff', map: awningCyanTexture, roughness: 0.76, metalness: 0.01 }),
  );
  awningCyan.name = 'arcade.material.awningCyan';
  const glass = resources.material(
    new THREE.MeshPhysicalMaterial({
      color: '#84dfe9',
      roughness: 0.08,
      transmission: 0,
      transparent: true,
      opacity: 0.24,
      thickness: 0.04,
      clearcoat: 1,
      clearcoatRoughness: 0.04,
      envMapIntensity: 1.2,
      depthWrite: false,
    }),
  );
  glass.name = 'arcade.material.glass';

  const bulbWarm = createEmissiveMaterial(resources, '#ffad35', '#ff7818', 2.15, 'bulbWarm');
  const bulbPink = createEmissiveMaterial(resources, '#ff4ba5', '#f11678', 1.82, 'bulbPink');
  const bulbCyan = createEmissiveMaterial(resources, '#4de8f0', '#0eaebd', 1.62, 'bulbCyan');
  const neonPink = createEmissiveMaterial(resources, '#ff3ca0', '#ef087d', 2.15, 'neonPink');
  const neonCyan = createEmissiveMaterial(resources, '#45eaf2', '#00acbd', 1.95, 'neonCyan');
  const neonWarm = createEmissiveMaterial(resources, '#ffc957', '#ff8b22', 1.88, 'neonWarm');
  const backdrop = resources.material(
    new THREE.MeshBasicMaterial({ map: backdropTexture, fog: false, side: THREE.DoubleSide }),
  );
  backdrop.name = 'arcade.material.backdrop';

  return {
    midnight,
    silhouette,
    podium,
    podiumEdge,
    brass,
    chrome,
    enamelPink,
    enamelCyan,
    fabricPink,
    fabricCyan,
    awningPink,
    awningCyan,
    glass,
    bulbWarm,
    bulbPink,
    bulbCyan,
    neonPink,
    neonCyan,
    neonWarm,
    backdrop,
  };
}

function createEmissiveMaterial(
  resources: OwnedResources,
  color: THREE.ColorRepresentation,
  emissive: THREE.ColorRepresentation,
  intensity: number,
  name: string,
): THREE.MeshStandardMaterial {
  const material = resources.material(
    new THREE.MeshStandardMaterial({
      color,
      emissive,
      emissiveIntensity: intensity,
      roughness: 0.24,
      metalness: 0.06,
      toneMapped: true,
    }),
  );
  material.name = `arcade.material.${name}`;
  return material;
}

function buildGround(context: BuildContext, parent: THREE.Group): void {
  const floor = createBoxMesh(
    context,
    'arcade.ground.boardwalk',
    [28, 0.14, 28],
    context.materials.midnight,
  );
  floor.position.set(0, -0.09, -3.2);
  floor.receiveShadow = true;
  parent.add(floor);

  const lowerPodium = createRoundedSlab(context, 'arcade.podium.lower', 10.8, 8.7, 1.15, 0.3, context.materials.podiumEdge);
  lowerPodium.position.set(0, 0.24, 0);
  lowerPodium.castShadow = true;
  lowerPodium.receiveShadow = true;
  parent.add(lowerPodium);

  const upperPodium = createRoundedSlab(context, 'arcade.podium.upper', 10.1, 8, 1, 0.18, context.materials.podium);
  upperPodium.position.set(0, MACHINE_FLOOR_Y, 0);
  upperPodium.receiveShadow = true;
  parent.add(upperPodium);

  const perimeter = createRoundedLoop(
    context,
    'arcade.podium.neonInlay',
    9.5,
    7.4,
    0.86,
    0.035,
    context.materials.neonWarm,
  );
  perimeter.position.y = MACHINE_FLOOR_Y + 0.016;
  perimeter.rotation.x = -Math.PI / 2;
  parent.add(perimeter);

  const machineShadow = createRoundedSlab(
    context,
    'arcade.podium.machineContact',
    6.1,
    4.25,
    0.6,
    0.025,
    context.materials.silhouette,
  );
  machineShadow.position.set(0, MACHINE_FLOOR_Y + 0.022, -0.02);
  parent.add(machineShadow);

  const stepDimensions: ReadonlyArray<readonly [number, number, number, number]> = [
    [6.5, 0.2, 0.72, 4.1],
    [5.8, 0.18, 0.66, 4.52],
    [5.1, 0.16, 0.58, 4.92],
  ];
  stepDimensions.forEach(([width, height, depth, z], index) => {
    const step = createBoxMesh(context, `arcade.podium.step.${index}`, [width, height, depth], context.materials.podiumEdge);
    step.position.set(0, height * 0.5 - 0.01, z);
    step.castShadow = true;
    step.receiveShadow = true;
    parent.add(step);

    const edge = createBoxMesh(context, `arcade.podium.step.${index}.edge`, [width * 0.94, 0.025, 0.035], context.materials.neonPink);
    edge.position.set(0, height + 0.012, z + depth * 0.47);
    parent.add(edge);
  });

  const runnerTexture = context.resources.texture(createRunnerTexture());
  const runnerMaterial = context.resources.material(
    new THREE.MeshPhysicalMaterial({
      color: '#ffffff',
      map: runnerTexture,
      roughness: 0.62,
      clearcoat: 0.08,
      clearcoatRoughness: 0.5,
    }),
  );
  runnerMaterial.name = 'arcade.material.runner';
  const runner = new THREE.Mesh(context.geometry.unitPlane, runnerMaterial);
  runner.name = 'arcade.ground.playTheTideRunner';
  runner.rotation.x = -Math.PI / 2;
  runner.scale.set(3.7, 5.6, 1);
  runner.position.set(0, 0.004, 6.9);
  runner.receiveShadow = true;
  parent.add(runner);

  for (const side of [-1, 1] as const) {
    const medallion = new THREE.Mesh(
      context.resources.geometry(new THREE.RingGeometry(0.62, 0.77, context.quality === 'full' ? 48 : 28)),
      side < 0 ? context.materials.neonPink : context.materials.neonCyan,
    );
    medallion.name = side < 0 ? 'arcade.ground.medallion.left' : 'arcade.ground.medallion.right';
    medallion.rotation.x = -Math.PI / 2;
    medallion.position.set(side * 4.1, MACHINE_FLOOR_Y + 0.025, 2.45);
    parent.add(medallion);
  }
}

function buildForeground(context: BuildContext, parent: THREE.Group): void {
  for (const side of [-1, 1] as const) {
    const sideName = side < 0 ? 'left' : 'right';
    const lowerCurve = new THREE.CatmullRomCurve3([
      new THREE.Vector3(side * 5.1, 0.82, 5.8),
      new THREE.Vector3(side * 5.25, 0.76, 4.2),
      new THREE.Vector3(side * 5.05, 0.7, 2.5),
      new THREE.Vector3(side * 4.82, 0.68, 1.35),
    ]);
    const upperCurve = new THREE.CatmullRomCurve3([
      new THREE.Vector3(side * 5.1, 1.32, 5.8),
      new THREE.Vector3(side * 5.25, 1.26, 4.2),
      new THREE.Vector3(side * 5.05, 1.18, 2.5),
      new THREE.Vector3(side * 4.82, 1.14, 1.35),
    ]);
    parent.add(createTubeMesh(context, `arcade.foreground.rail.${sideName}.lower`, lowerCurve, 0.055, context.materials.brass));
    parent.add(createTubeMesh(context, `arcade.foreground.rail.${sideName}.upper`, upperCurve, 0.065, context.materials.chrome));

    [1.45, 3.1, 4.7, 5.75].forEach((z, index) => {
      const x = side * (4.82 + Math.sin(index * 0.9) * 0.32);
      const post = createArtDecoBollard(context, `arcade.foreground.bollard.${sideName}.${index}`);
      post.position.set(x, 0, z);
      parent.add(post);
    });

    const ropeCurve = new THREE.CatmullRomCurve3([
      new THREE.Vector3(side * 4.86, 1.12, 5.7),
      new THREE.Vector3(side * 4.96, 0.72, 4.72),
      new THREE.Vector3(side * 4.96, 0.72, 3.12),
      new THREE.Vector3(side * 4.79, 1.04, 1.5),
    ]);
    parent.add(
      createTubeMesh(
        context,
        `arcade.foreground.velvetRope.${sideName}`,
        ropeCurve,
        0.045,
        side < 0 ? context.materials.fabricPink : context.materials.fabricCyan,
      ),
    );
  }

  const frontBulbs: THREE.Vector3[] = [];
  for (let index = 0; index < 18; index += 1) {
    const t = index / 17;
    frontBulbs.push(new THREE.Vector3(THREE.MathUtils.lerp(-3.2, 3.2, t), 0.16, 5.23));
  }
  parent.add(createBulbInstances(context, 'arcade.foreground.stepBulbs.warm', frontBulbs.filter((_, i) => i % 2 === 0), context.materials.bulbWarm, 0.065));
  parent.add(createBulbInstances(context, 'arcade.foreground.stepBulbs.pink', frontBulbs.filter((_, i) => i % 2 === 1), context.materials.bulbPink, 0.065));
}

function buildMidground(context: BuildContext, parent: THREE.Group): void {
  const arch = createBoardwalkArch(context);
  arch.position.set(0, 0, -5.4);
  parent.add(arch);

  const leftBooth = createPrizeBooth(context, 'PRIZE PIER', 'TOKENS & TREASURES', 'pink');
  leftBooth.position.set(-6.75, 0, -4.15);
  leftBooth.rotation.y = 0.13;
  parent.add(leftBooth);

  const rightBooth = createPrizeBooth(context, 'BONUS BAY', 'EVERY COIN COUNTS', 'cyan');
  rightBooth.position.set(6.75, 0, -4.45);
  rightBooth.rotation.y = -0.13;
  parent.add(rightBooth);

  for (const side of [-1, 1] as const) {
    const tower = createDecoLightTower(context, side < 0 ? 'left' : 'right');
    tower.position.set(side * 9.1, 0, -6.4);
    tower.scale.setScalar(side < 0 ? 0.92 : 1.06);
    parent.add(tower);
  }

  buildCanopyStrings(context, parent);
}

function buildFarLayer(context: BuildContext, parent: THREE.Group): void {
  const backdrop = new THREE.Mesh(context.geometry.unitPlane, context.materials.backdrop);
  backdrop.name = 'arcade.far.gradientBackdrop';
  backdrop.scale.set(31, 14, 1);
  backdrop.position.set(0, 6.7, -16.8);
  parent.add(backdrop);

  const skyline = createSkyline(context);
  skyline.position.set(0, 0.2, -14.2);
  parent.add(skyline);

  const ferris = createFerrisWheel(context);
  ferris.root.position.set(-9.4, 0.2, -11.4);
  parent.add(ferris.root);
  context.animation.ferris = { wheel: ferris.wheel, gondolas: ferris.gondolas };

  const pavilion = createNeonPavilion(context);
  pavilion.position.set(9.2, 0, -11.6);
  parent.add(pavilion);

  const bokehSets: ReadonlyArray<readonly [THREE.ColorRepresentation, number, number]> = [
    ['#ff3d9d', context.quality === 'full' ? 34 : 18, 0.64],
    ['#48dce8', context.quality === 'full' ? 28 : 14, 0.52],
    ['#ffbd4c', context.quality === 'full' ? 30 : 16, 0.46],
  ];
  bokehSets.forEach(([color, count, size], index) => {
    const points = createBokehPoints(context, `arcade.far.bokeh.${index}`, color, count, size);
    parent.add(points);
    context.animation.bokeh.push(points);
  });
}

function buildLighting(context: BuildContext, parent: THREE.Group, quality: ArcadeSceneQuality): void {
  const hemisphere = new THREE.HemisphereLight('#ffd9d1', '#120b21', 1.35);
  hemisphere.name = 'arcade.light.hemisphere';
  parent.add(hemisphere);

  const key = new THREE.DirectionalLight('#ffd7aa', 2.85);
  key.name = 'arcade.light.key';
  key.position.set(-6.5, 10.5, 7.5);
  key.castShadow = true;
  key.shadow.mapSize.set(quality === 'full' ? 2048 : 1024, quality === 'full' ? 2048 : 1024);
  key.shadow.camera.near = 1;
  key.shadow.camera.far = 28;
  key.shadow.camera.left = -8;
  key.shadow.camera.right = 8;
  key.shadow.camera.top = 9;
  key.shadow.camera.bottom = -3;
  key.shadow.bias = -0.00035;
  key.target.position.set(0, 2.4, 0);
  parent.add(key, key.target);

  const pinkFill = new THREE.RectAreaLight('#ff2f94', 12, 4.5, 5.5);
  pinkFill.name = 'arcade.light.pinkFill';
  pinkFill.position.set(-6.8, 4.4, 3.8);
  pinkFill.lookAt(0, 2.7, 0);
  parent.add(pinkFill);

  const cyanFill = new THREE.RectAreaLight('#26d9e7', 10.5, 4.2, 5.2);
  cyanFill.name = 'arcade.light.cyanFill';
  cyanFill.position.set(6.6, 4.1, 2.8);
  cyanFill.lookAt(0, 2.8, -0.3);
  parent.add(cyanFill);

  const marqueeRim = new THREE.SpotLight('#ff4ba5', 9.5, 21, Math.PI * 0.2, 0.72, 1.5);
  marqueeRim.name = 'arcade.light.marqueeRim';
  marqueeRim.position.set(-4.8, 7.4, -4.4);
  marqueeRim.target.position.set(0, 3.35, 0.35);
  parent.add(marqueeRim, marqueeRim.target);
  context.animation.practicalLights.push(marqueeRim);

  const glassRim = new THREE.SpotLight('#46e9f2', 9.5, 19, Math.PI * 0.18, 0.7, 1.6);
  glassRim.name = 'arcade.light.glassRim';
  glassRim.position.set(4.6, 6.2, -3.7);
  glassRim.target.position.set(0, 2.8, 0.45);
  parent.add(glassRim, glassRim.target);
  context.animation.practicalLights.push(glassRim);
}

function createArtDecoBollard(context: BuildContext, name: string): THREE.Group {
  const root = new THREE.Group();
  root.name = name;
  const foot = createCylinderMesh(context, `${name}.foot`, 0.22, 0.28, context.materials.brass);
  foot.position.y = 0.14;
  foot.castShadow = true;
  root.add(foot);
  const stem = createCylinderMesh(context, `${name}.stem`, 0.075, 0.82, context.materials.chrome);
  stem.position.y = 0.67;
  stem.castShadow = true;
  root.add(stem);
  const collar = createCylinderMesh(context, `${name}.collar`, 0.15, 0.1, context.materials.brass);
  collar.position.y = 1.02;
  root.add(collar);
  const globe = new THREE.Mesh(context.geometry.bulb, context.materials.bulbWarm);
  globe.name = `${name}.globe`;
  globe.scale.setScalar(0.16);
  globe.position.y = 1.18;
  root.add(globe);
  return root;
}

function createPrizeBooth(
  context: BuildContext,
  title: string,
  subtitle: string,
  palette: 'pink' | 'cyan',
): THREE.Group {
  const root = new THREE.Group();
  const id = title.toLowerCase().replaceAll(' ', '-');
  root.name = `arcade.booth.${id}`;
  const enamel = palette === 'pink' ? context.materials.enamelPink : context.materials.enamelCyan;
  const fabric = palette === 'pink' ? context.materials.fabricPink : context.materials.fabricCyan;
  const awning = palette === 'pink' ? context.materials.awningPink : context.materials.awningCyan;
  const neon = palette === 'pink' ? context.materials.neonPink : context.materials.neonCyan;

  const base = createRoundedSlab(context, `${root.name}.base`, 4.65, 3, 0.38, 0.32, context.materials.podiumEdge);
  base.position.y = 0.32;
  base.castShadow = true;
  base.receiveShadow = true;
  root.add(base);

  const back = createBoxMesh(context, `${root.name}.back`, [4.3, 3.35, 0.28], context.materials.silhouette);
  back.position.set(0, 2.05, -0.72);
  back.castShadow = true;
  root.add(back);

  for (const side of [-1, 1] as const) {
    const post = createBoxMesh(context, `${root.name}.post.${side < 0 ? 'left' : 'right'}`, [0.25, 3.3, 0.25], context.materials.brass);
    post.position.set(side * 2.03, 2.02, 0.75);
    post.castShadow = true;
    root.add(post);
    const inner = createBoxMesh(context, `${post.name}.inner`, [0.11, 2.85, 0.29], enamel);
    inner.position.set(side * 2.03, 2, 0.77);
    root.add(inner);
  }

  const counter = createBoxMesh(context, `${root.name}.counter`, [4.5, 0.42, 0.82], context.materials.podiumEdge);
  counter.position.set(0, 1.1, 0.92);
  counter.castShadow = true;
  counter.receiveShadow = true;
  root.add(counter);
  const counterTrim = createBoxMesh(context, `${root.name}.counterTrim`, [4.25, 0.09, 0.09], context.materials.chrome);
  counterTrim.position.set(0, 1.28, 1.34);
  root.add(counterTrim);

  const displayGlass = createBoxMesh(context, `${root.name}.displayGlass`, [3.76, 1.34, 0.06], context.materials.glass);
  displayGlass.position.set(0, 2.02, 0.69);
  root.add(displayGlass);
  const shelf = createBoxMesh(context, `${root.name}.shelf`, [3.68, 0.07, 0.72], context.materials.chrome);
  shelf.position.set(0, 1.48, 0.44);
  root.add(shelf);

  const canopy = createBoxMesh(context, `${root.name}.canopy`, [4.82, 0.34, 2.82], fabric);
  canopy.position.set(0, 3.45, 0.05);
  canopy.rotation.x = -0.055;
  canopy.castShadow = true;
  root.add(canopy);
  const awningFace = new THREE.Mesh(context.geometry.unitPlane, awning);
  awningFace.name = `${root.name}.awning`;
  awningFace.scale.set(4.65, 0.72, 1);
  awningFace.position.set(0, 3.15, 1.45);
  awningFace.rotation.x = -0.14;
  root.add(awningFace);

  const scallopCount = context.quality === 'full' ? 10 : 7;
  const scallopGeometry = context.resources.geometry(new THREE.CircleGeometry(0.24, 14, Math.PI, Math.PI));
  const scallops = new THREE.InstancedMesh(scallopGeometry, awning, scallopCount);
  scallops.name = `${root.name}.scallops`;
  const scallopMatrix = new THREE.Matrix4();
  for (let index = 0; index < scallopCount; index += 1) {
    scallopMatrix.makeTranslation(
      THREE.MathUtils.lerp(-2.08, 2.08, index / (scallopCount - 1)),
      2.84,
      1.475,
    );
    scallops.setMatrixAt(index, scallopMatrix);
  }
  scallops.instanceMatrix.needsUpdate = true;
  root.add(scallops);

  const sign = createDiegeticSign(context, `${root.name}.sign`, title, subtitle, palette, 3.82, 1.16);
  sign.root.position.set(0, 4.28, 0.66);
  root.add(sign.root);
  context.animation.signs.push({
    object: sign.root,
    material: sign.faceMaterial,
    baseY: sign.root.position.y,
    phase: palette === 'pink' ? 0.4 : 2.4,
    amplitude: 0.025,
  });

  const iconMaterials = [context.materials.neonWarm, neon, context.materials.chrome] as const;
  for (let index = 0; index < 5; index += 1) {
    const prize = createBoothPrize(context, `${root.name}.prize.${index}`, index, iconMaterials[index % iconMaterials.length]);
    prize.position.set(THREE.MathUtils.lerp(-1.45, 1.45, index / 4), 1.83 + (index % 2) * 0.12, 0.62);
    prize.rotation.z = (index - 2) * 0.045;
    root.add(prize);
  }

  const trimCurve = new THREE.CatmullRomCurve3(
    [
      new THREE.Vector3(-2.24, 3.58, 1.1),
      new THREE.Vector3(0, 3.72, 1.32),
      new THREE.Vector3(2.24, 3.58, 1.1),
    ],
  );
  root.add(createTubeMesh(context, `${root.name}.canopyNeon`, trimCurve, 0.035, neon));
  return root;
}

function createBoothPrize(
  context: BuildContext,
  name: string,
  variant: number,
  material: THREE.Material,
): THREE.Group {
  const root = new THREE.Group();
  root.name = name;
  if (variant % 3 === 0) {
    const ring = new THREE.Mesh(
      context.resources.geometry(new THREE.TorusGeometry(0.22, 0.055, 8, 24)),
      material,
    );
    ring.name = `${name}.ring`;
    root.add(ring);
    const core = new THREE.Mesh(
      context.resources.geometry(new THREE.OctahedronGeometry(0.11, 0)),
      context.materials.enamelPink,
    );
    core.name = `${name}.core`;
    root.add(core);
  } else if (variant % 3 === 1) {
    const star = new THREE.Mesh(
      context.resources.geometry(new THREE.OctahedronGeometry(0.23, 0)),
      material,
    );
    star.name = `${name}.star`;
    star.scale.y = 1.25;
    root.add(star);
  } else {
    const capsule = createCylinderMesh(context, `${name}.capsule`, 0.16, 0.42, material);
    capsule.rotation.z = Math.PI / 2;
    root.add(capsule);
    const band = new THREE.Mesh(
      context.resources.geometry(new THREE.TorusGeometry(0.165, 0.025, 6, 18)),
      context.materials.brass,
    );
    band.name = `${name}.band`;
    band.rotation.y = Math.PI / 2;
    root.add(band);
  }
  return root;
}

function createBoardwalkArch(context: BuildContext): THREE.Group {
  const root = new THREE.Group();
  root.name = 'arcade.midground.boardwalkArch';
  for (const side of [-1, 1] as const) {
    const sideName = side < 0 ? 'left' : 'right';
    const base = createRoundedSlab(context, `${root.name}.base.${sideName}`, 0.9, 1.35, 0.2, 0.28, context.materials.podiumEdge);
    base.position.set(side * 4.65, 0.2, 0);
    root.add(base);
    const post = createBoxMesh(context, `${root.name}.post.${sideName}`, [0.42, 5.15, 0.48], context.materials.brass);
    post.position.set(side * 4.65, 2.8, 0);
    post.castShadow = true;
    root.add(post);
    const inset = createBoxMesh(
      context,
      `${root.name}.post.${sideName}.inset`,
      [0.16, 4.55, 0.51],
      side < 0 ? context.materials.neonPink : context.materials.neonCyan,
    );
    inset.position.set(side * 4.65, 2.82, 0.02);
    root.add(inset);
    const fin = createBoxMesh(context, `${root.name}.fin.${sideName}`, [1.1, 0.18, 0.62], context.materials.chrome);
    fin.position.set(side * 4.65, 5.28, 0);
    root.add(fin);
  }

  const archPoints: THREE.Vector3[] = [];
  const archSegments = context.quality === 'full' ? 28 : 18;
  for (let index = 0; index <= archSegments; index += 1) {
    const angle = (index / archSegments) * Math.PI;
    archPoints.push(new THREE.Vector3(Math.cos(angle) * 4.65, 5.25 + Math.sin(angle) * 2.15, 0));
  }
  const archCurve = new THREE.CatmullRomCurve3(archPoints);
  root.add(createTubeMesh(context, `${root.name}.outerArc`, archCurve, 0.13, context.materials.brass));
  const innerCurve = new THREE.CatmullRomCurve3(archPoints.map((point) => new THREE.Vector3(point.x * 0.955, point.y - 0.18, 0.05)));
  root.add(createTubeMesh(context, `${root.name}.innerArc`, innerCurve, 0.045, context.materials.neonPink));

  const archBulbs = archPoints.filter((_, index) => index % 2 === 0).map((point) => point.clone().add(new THREE.Vector3(0, 0, 0.12)));
  root.add(createBulbInstances(context, `${root.name}.bulbs`, archBulbs, context.materials.bulbWarm, 0.075));

  const sign = createDiegeticSign(context, `${root.name}.sign`, 'LUCKY TIDE', 'MIDNIGHT ARCADE', 'pink', 4.3, 1.22);
  sign.root.position.set(0, 7.25, 0.16);
  root.add(sign.root);
  context.animation.signs.push({
    object: sign.root,
    material: sign.faceMaterial,
    baseY: sign.root.position.y,
    phase: 1.25,
    amplitude: 0.018,
  });
  return root;
}

function createDiegeticSign(
  context: BuildContext,
  name: string,
  title: string,
  subtitle: string,
  palette: 'pink' | 'cyan',
  width: number,
  height: number,
): { root: THREE.Group; faceMaterial: THREE.MeshStandardMaterial } {
  const root = new THREE.Group();
  root.name = name;
  const radius = Math.min(0.28, height * 0.24);
  const shape = createRoundedRectShape(width, height, radius);
  const bodyGeometry = context.resources.geometry(
    new THREE.ExtrudeGeometry(shape, {
      depth: 0.16,
      bevelEnabled: true,
      bevelSize: 0.055,
      bevelThickness: 0.035,
      bevelSegments: context.quality === 'full' ? 2 : 1,
      curveSegments: context.quality === 'full' ? 8 : 5,
    }),
  );
  const body = new THREE.Mesh(bodyGeometry, palette === 'pink' ? context.materials.enamelPink : context.materials.enamelCyan);
  body.name = `${name}.body`;
  body.position.z = -0.08;
  body.castShadow = true;
  root.add(body);

  const texture = context.resources.texture(createSignTexture(title, subtitle, palette));
  const faceMaterial = context.resources.material(
    new THREE.MeshStandardMaterial({
      color: '#ffffff',
      map: texture,
      emissiveMap: texture,
      emissive: '#ffffff',
      emissiveIntensity: 0.42,
      roughness: 0.28,
      metalness: 0.08,
    }),
  );
  faceMaterial.name = `${name}.faceMaterial`;
  const face = new THREE.Mesh(context.geometry.unitPlane, faceMaterial);
  face.name = `${name}.face`;
  face.scale.set(width - 0.22, height - 0.2, 1);
  face.position.z = 0.115;
  root.add(face);

  const perimeterPoints = createRoundedRectPerimeter(width - 0.1, height - 0.08, radius, 8, 0.145);
  const perimeterCurve = new THREE.CatmullRomCurve3(perimeterPoints, true, 'centripetal');
  root.add(
    createTubeMesh(
      context,
      `${name}.neonFrame`,
      perimeterCurve,
      0.026,
      palette === 'pink' ? context.materials.neonPink : context.materials.neonCyan,
      true,
    ),
  );

  const bulbPoints = sampleRoundedRect(width + 0.04, height + 0.04, radius + 0.04, context.quality === 'full' ? 30 : 20, 0.18);
  root.add(createBulbInstances(context, `${name}.bulbs`, bulbPoints, context.materials.bulbWarm, 0.055));
  return { root, faceMaterial };
}

function createDecoLightTower(context: BuildContext, sideName: string): THREE.Group {
  const root = new THREE.Group();
  root.name = `arcade.midground.lightTower.${sideName}`;
  const base = createRoundedSlab(context, `${root.name}.base`, 1.45, 1.45, 0.16, 0.3, context.materials.podiumEdge);
  base.position.y = 0.2;
  root.add(base);
  const shaft = createBoxMesh(context, `${root.name}.shaft`, [0.62, 4.3, 0.62], context.materials.silhouette);
  shaft.position.y = 2.45;
  shaft.castShadow = true;
  root.add(shaft);
  for (let level = 0; level < 3; level += 1) {
    const collar = createBoxMesh(
      context,
      `${root.name}.collar.${level}`,
      [1.05 - level * 0.16, 0.15, 1.05 - level * 0.16],
      context.materials.brass,
    );
    collar.position.y = 1.25 + level * 1.45;
    root.add(collar);
  }
  const crown = createCylinderMesh(context, `${root.name}.crown`, 0.46, 0.72, context.materials.chrome);
  crown.position.y = 4.9;
  root.add(crown);
  const beacon = new THREE.Mesh(context.geometry.bulb, sideName === 'left' ? context.materials.bulbPink : context.materials.bulbCyan);
  beacon.name = `${root.name}.beacon`;
  beacon.scale.set(0.31, 0.5, 0.31);
  beacon.position.y = 5.55;
  root.add(beacon);
  return root;
}

function buildCanopyStrings(context: BuildContext, parent: THREE.Group): void {
  for (const side of [-1, 1] as const) {
    const sideName = side < 0 ? 'left' : 'right';
    const curve = new THREE.CatmullRomCurve3([
      new THREE.Vector3(side * 4.35, 4.2, 4.8),
      new THREE.Vector3(side * 4.7, 4.65, 1.8),
      new THREE.Vector3(side * 4.7, 5.2, -1.6),
      new THREE.Vector3(side * 4.65, 5.45, -5.35),
    ]);
    parent.add(createTubeMesh(context, `arcade.midground.canopyCable.${sideName}`, curve, 0.018, context.materials.silhouette));
    const points = curve.getPoints(context.quality === 'full' ? 22 : 14).slice(1, -1);
    parent.add(
      createBulbInstances(
        context,
        `arcade.midground.canopyBulbs.${sideName}`,
        points.filter((_, index) => index % 2 === 0),
        side < 0 ? context.materials.bulbPink : context.materials.bulbCyan,
        0.062,
      ),
    );
    parent.add(
      createBulbInstances(
        context,
        `arcade.midground.canopyBulbs.${sideName}.warm`,
        points.filter((_, index) => index % 2 === 1),
        context.materials.bulbWarm,
        0.055,
      ),
    );
  }
}

function createSkyline(context: BuildContext): THREE.Group {
  const root = new THREE.Group();
  root.name = 'arcade.far.skyline';
  const rearShape = createSkylineShape(context.random, 31, 5.8, 15);
  const rearGeometry = context.resources.geometry(new THREE.ShapeGeometry(rearShape));
  const rearMaterial = context.resources.material(
    new THREE.MeshBasicMaterial({ color: '#10071c', fog: false }),
  );
  rearMaterial.name = 'arcade.material.skylineRear';
  const rear = new THREE.Mesh(rearGeometry, rearMaterial);
  rear.name = 'arcade.far.skyline.rear';
  rear.position.set(0, 0.1, -0.28);
  root.add(rear);

  const frontShape = createSkylineShape(context.random, 28, 4.5, 12);
  const front = new THREE.Mesh(context.resources.geometry(new THREE.ShapeGeometry(frontShape)), context.materials.silhouette);
  front.name = 'arcade.far.skyline.front';
  front.position.y = 0;
  root.add(front);

  const windowGeometry = context.geometry.unitPlane;
  const windowCount = context.quality === 'full' ? 54 : 28;
  const warmWindows = new THREE.InstancedMesh(windowGeometry, context.materials.bulbWarm, windowCount);
  warmWindows.name = 'arcade.far.skyline.windows';
  const dummy = new THREE.Object3D();
  for (let index = 0; index < windowCount; index += 1) {
    dummy.position.set(-12.7 + context.random() * 25.4, 0.65 + context.random() * 3.2, 0.035);
    dummy.scale.set(0.08 + context.random() * 0.07, 0.13 + context.random() * 0.18, 1);
    dummy.updateMatrix();
    warmWindows.setMatrixAt(index, dummy.matrix);
  }
  warmWindows.instanceMatrix.needsUpdate = true;
  root.add(warmWindows);
  return root;
}

function createFerrisWheel(context: BuildContext): {
  root: THREE.Group;
  wheel: THREE.Group;
  gondolas: THREE.Group[];
} {
  const root = new THREE.Group();
  root.name = 'arcade.far.ferris.root';
  const wheel = new THREE.Group();
  wheel.name = 'arcade.far.ferris.wheel';
  wheel.position.y = 5.4;
  root.add(wheel);
  const radius = 3.65;
  const segments = context.quality === 'full' ? 56 : 36;

  const outerRing = new THREE.Mesh(
    context.resources.geometry(new THREE.TorusGeometry(radius, 0.095, 8, segments)),
    context.materials.brass,
  );
  outerRing.name = 'arcade.far.ferris.outerRing';
  wheel.add(outerRing);
  const innerRing = new THREE.Mesh(
    context.resources.geometry(new THREE.TorusGeometry(radius * 0.88, 0.045, 6, segments)),
    context.materials.neonPink,
  );
  innerRing.name = 'arcade.far.ferris.innerRing';
  wheel.add(innerRing);

  const spokeCount = context.quality === 'full' ? 12 : 8;
  const gondolas: THREE.Group[] = [];
  const bulbPoints: THREE.Vector3[] = [];
  for (let index = 0; index < spokeCount; index += 1) {
    const angle = (index / spokeCount) * Math.PI * 2;
    const end = new THREE.Vector3(Math.cos(angle) * radius * 0.96, Math.sin(angle) * radius * 0.96, 0);
    const spoke = createBeamBetween(
      context,
      `arcade.far.ferris.spoke.${index}`,
      new THREE.Vector3(0, 0, 0),
      end,
      0.025,
      context.materials.chrome,
    );
    wheel.add(spoke);

    const gondola = new THREE.Group();
    gondola.name = `arcade.far.ferris.gondola.${index}`;
    gondola.position.copy(end).multiplyScalar(1.03);
    const arm = createBoxMesh(context, `${gondola.name}.arm`, [0.035, 0.46, 0.035], context.materials.chrome);
    arm.position.y = -0.21;
    gondola.add(arm);
    const cabin = createRoundedSlab(
      context,
      `${gondola.name}.cabin`,
      0.58,
      0.3,
      0.08,
      0.22,
      index % 2 === 0 ? context.materials.enamelPink : context.materials.enamelCyan,
    );
    cabin.rotation.x = Math.PI / 2;
    cabin.position.y = -0.58;
    gondola.add(cabin);
    const canopy = createBoxMesh(context, `${gondola.name}.canopy`, [0.68, 0.06, 0.38], context.materials.brass);
    canopy.position.y = -0.36;
    gondola.add(canopy);
    wheel.add(gondola);
    gondolas.push(gondola);
  }

  const bulbCount = context.quality === 'full' ? 42 : 24;
  for (let index = 0; index < bulbCount; index += 1) {
    const angle = (index / bulbCount) * Math.PI * 2;
    bulbPoints.push(new THREE.Vector3(Math.cos(angle) * radius, Math.sin(angle) * radius, 0.08));
  }
  wheel.add(createBulbInstances(context, 'arcade.far.ferris.bulbs', bulbPoints, context.materials.bulbWarm, 0.07));

  const hub = createCylinderMesh(context, 'arcade.far.ferris.hub', 0.28, 0.42, context.materials.brass);
  hub.rotation.x = Math.PI / 2;
  wheel.add(hub);
  for (const side of [-1, 1] as const) {
    const support = createBeamBetween(
      context,
      `arcade.far.ferris.support.${side < 0 ? 'left' : 'right'}`,
      new THREE.Vector3(side * 2.45, 0.2, 0.12),
      new THREE.Vector3(side * 0.34, 5.35, 0.12),
      0.11,
      context.materials.brass,
    );
    root.add(support);
  }
  const footing = createRoundedSlab(context, 'arcade.far.ferris.footing', 5.8, 1.4, 0.2, 0.3, context.materials.podiumEdge);
  footing.position.y = 0.2;
  root.add(footing);
  return { root, wheel, gondolas };
}

function createNeonPavilion(context: BuildContext): THREE.Group {
  const root = new THREE.Group();
  root.name = 'arcade.far.neonPavilion';
  const base = createRoundedSlab(context, `${root.name}.base`, 6.2, 3.3, 0.3, 0.6, context.materials.podiumEdge);
  base.position.y = 0.25;
  root.add(base);

  const center = createBoxMesh(context, `${root.name}.center`, [2.25, 4.2, 1.75], context.materials.silhouette);
  center.position.y = 2.45;
  root.add(center);
  const centerRoof = new THREE.Mesh(
    context.resources.geometry(new THREE.ConeGeometry(1.75, 2.3, 6)),
    context.materials.enamelCyan,
  );
  centerRoof.name = `${root.name}.centerRoof`;
  centerRoof.position.y = 5.62;
  centerRoof.rotation.y = Math.PI / 6;
  root.add(centerRoof);

  for (const side of [-1, 1] as const) {
    const wing = createBoxMesh(context, `${root.name}.wing.${side < 0 ? 'left' : 'right'}`, [1.85, 2.65, 1.55], context.materials.silhouette);
    wing.position.set(side * 2.05, 1.65, 0);
    root.add(wing);
    const roof = new THREE.Mesh(
      context.resources.geometry(new THREE.ConeGeometry(1.3, 1.75, 6)),
      side < 0 ? context.materials.enamelPink : context.materials.enamelCyan,
    );
    roof.name = `${wing.name}.roof`;
    roof.position.set(side * 2.05, 3.85, 0);
    roof.rotation.y = Math.PI / 6;
    root.add(roof);
  }

  const archPoints: THREE.Vector3[] = [];
  for (let index = 0; index <= 18; index += 1) {
    const angle = (index / 18) * Math.PI;
    archPoints.push(new THREE.Vector3(Math.cos(angle) * 2.45, 2.4 + Math.sin(angle) * 2.1, 0.95));
  }
  root.add(
    createTubeMesh(
      context,
      `${root.name}.neonArch`,
      new THREE.CatmullRomCurve3(archPoints),
      0.065,
      context.materials.neonCyan,
    ),
  );
  const fin = createBoxMesh(context, `${root.name}.spire`, [0.1, 2.1, 0.1], context.materials.brass);
  fin.position.y = 7.25;
  root.add(fin);
  const beacon = new THREE.Mesh(context.geometry.bulb, context.materials.bulbPink);
  beacon.name = `${root.name}.beacon`;
  beacon.scale.setScalar(0.22);
  beacon.position.y = 8.35;
  root.add(beacon);
  return root;
}

function createBokehPoints(
  context: BuildContext,
  name: string,
  color: THREE.ColorRepresentation,
  count: number,
  size: number,
): THREE.Points {
  const positions = new Float32Array(count * 3);
  for (let index = 0; index < count; index += 1) {
    const sideBias = context.random() < 0.5 ? -1 : 1;
    positions[index * 3] = sideBias * (2.7 + context.random() * 11.7);
    positions[index * 3 + 1] = 1.2 + context.random() * 8.7;
    positions[index * 3 + 2] = -8.5 - context.random() * 7.2;
  }
  const geometry = context.resources.geometry(new THREE.BufferGeometry());
  geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  const texture = context.resources.texture(createBokehTexture());
  const material = context.resources.material(
    new THREE.PointsMaterial({
      color,
      map: texture,
      size,
      sizeAttenuation: true,
      transparent: true,
      opacity: 0.76,
      alphaTest: 0.02,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      fog: true,
    }),
  );
  material.name = `${name}.material`;
  const points = new THREE.Points(geometry, material);
  points.name = name;
  return points;
}

function buildSceneSockets(
  parent: THREE.Group,
  machineRuntime: CoinPusherMachineRuntime,
): Record<string, THREE.Object3D> {
  const sockets: Record<string, THREE.Object3D> = {};
  const add = (id: string, position: readonly [number, number, number], role: string): THREE.Object3D => {
    const socket = new THREE.Object3D();
    socket.name = id;
    socket.position.set(...position);
    socket.userData.role = role;
    parent.add(socket);
    sockets[id] = socket;
    return socket;
  };
  add('socket.arcade.machineOrigin', [0, MACHINE_FLOOR_Y, 0], 'machine-origin');
  add('socket.arcade.cameraTarget', [0, 3.08, 0.25], 'camera-target');
  add('socket.arcade.cameraHero', [0, 5.15, 11.25], 'camera-position');
  add('socket.arcade.cameraClose', [0, 4.08, 7.55], 'camera-position');
  add('socket.arcade.keyLight', [-6.5, 10.5, 7.5], 'light-position');
  add('socket.arcade.foregroundLeft', [-5.2, 0.6, 4.5], 'foreground-anchor');
  add('socket.arcade.foregroundRight', [5.2, 0.6, 4.5], 'foreground-anchor');
  add('socket.arcade.rearCenter', [0, 5.4, -6], 'midground-anchor');
  for (const [id, socket] of Object.entries(machineRuntime.sockets)) {
    sockets[`machine.${id}`] = socket;
  }
  return sockets;
}

function collectDecorativeInterior(runtime: CoinPusherMachineRuntime): THREE.Object3D[] {
  const objects = new Set<THREE.Object3D>();
  const previewIds = [
    'coins.fixed.nickel',
    'coins.fixed.brass',
    'coins.pusher.nickel',
    'coins.pusher.brass',
  ];
  for (const id of previewIds) {
    const object = runtime.meshes[id];
    if (object) objects.add(object);
  }
  for (const [id, object] of Object.entries(runtime.meshes)) {
    if (id.startsWith('prize.')) objects.add(object);
  }
  const pusher = runtime.nodes['pusher.pivot'];
  if (pusher) objects.add(pusher);
  return [...objects];
}

function isWebGLRenderer(value: THREE.WebGLRenderer | ArcadeSceneOptions): value is THREE.WebGLRenderer {
  return Boolean((value as unknown as { isWebGLRenderer?: boolean }).isWebGLRenderer);
}

function copyVectorLike(
  target: THREE.Vector3,
  value: THREE.Vector3 | readonly [number, number, number],
): THREE.Vector3 {
  return value instanceof THREE.Vector3 ? target.copy(value) : target.set(value[0], value[1], value[2]);
}

function createRandom(seed: number): () => number {
  let state = seed >>> 0;
  return () => {
    state += 0x6d2b79f5;
    let value = state;
    value = Math.imul(value ^ (value >>> 15), value | 1);
    value ^= value + Math.imul(value ^ (value >>> 7), value | 61);
    return ((value ^ (value >>> 14)) >>> 0) / 4294967296;
  };
}

function createBoxMesh(
  context: BuildContext,
  name: string,
  size: readonly [number, number, number],
  material: THREE.Material,
): THREE.Mesh {
  const mesh = new THREE.Mesh(context.geometry.unitBox, material);
  mesh.name = name;
  mesh.scale.set(size[0], size[1], size[2]);
  return mesh;
}

function createCylinderMesh(
  context: BuildContext,
  name: string,
  radius: number,
  height: number,
  material: THREE.Material,
): THREE.Mesh {
  const mesh = new THREE.Mesh(context.geometry.unitCylinder, material);
  mesh.name = name;
  mesh.scale.set(radius, height, radius);
  return mesh;
}

function createRoundedSlab(
  context: BuildContext,
  name: string,
  width: number,
  depth: number,
  radius: number,
  height: number,
  material: THREE.Material,
): THREE.Mesh {
  const shape = createRoundedRectShape(width, depth, Math.min(radius, width * 0.48, depth * 0.48));
  const geometry = context.resources.geometry(
    new THREE.ExtrudeGeometry(shape, {
      depth: height,
      bevelEnabled: true,
      bevelSize: Math.min(0.045, height * 0.18),
      bevelThickness: Math.min(0.035, height * 0.15),
      bevelSegments: context.quality === 'full' ? 2 : 1,
      curveSegments: context.quality === 'full' ? 8 : 5,
    }),
  );
  const mesh = new THREE.Mesh(geometry, material);
  mesh.name = name;
  mesh.rotation.x = Math.PI / 2;
  return mesh;
}

function createRoundedLoop(
  context: BuildContext,
  name: string,
  width: number,
  height: number,
  radius: number,
  tubeRadius: number,
  material: THREE.Material,
): THREE.Mesh {
  const points = createRoundedRectPerimeter(width, height, radius, context.quality === 'full' ? 10 : 6, 0);
  return createTubeMesh(
    context,
    name,
    new THREE.CatmullRomCurve3(points, true, 'centripetal'),
    tubeRadius,
    material,
    true,
  );
}

function createTubeMesh(
  context: BuildContext,
  name: string,
  curve: THREE.Curve<THREE.Vector3>,
  radius: number,
  material: THREE.Material,
  closed = false,
): THREE.Mesh {
  const geometry = context.resources.geometry(
    new THREE.TubeGeometry(
      curve,
      context.quality === 'full' ? 72 : 42,
      radius,
      context.quality === 'full' ? 8 : 5,
      closed,
    ),
  );
  const mesh = new THREE.Mesh(geometry, material);
  mesh.name = name;
  return mesh;
}

function createBulbInstances(
  context: BuildContext,
  name: string,
  positions: readonly THREE.Vector3[],
  material: THREE.Material,
  radius: number,
): THREE.InstancedMesh {
  const bulbs = new THREE.InstancedMesh(context.geometry.bulb, material, positions.length);
  bulbs.name = name;
  const dummy = new THREE.Object3D();
  positions.forEach((position, index) => {
    dummy.position.copy(position);
    const scale = radius * (0.92 + (index % 3) * 0.04);
    dummy.scale.setScalar(scale);
    dummy.updateMatrix();
    bulbs.setMatrixAt(index, dummy.matrix);
  });
  bulbs.instanceMatrix.needsUpdate = true;
  bulbs.frustumCulled = true;
  return bulbs;
}

function createBeamBetween(
  context: BuildContext,
  name: string,
  start: THREE.Vector3,
  end: THREE.Vector3,
  radius: number,
  material: THREE.Material,
): THREE.Mesh {
  const direction = end.clone().sub(start);
  const length = direction.length();
  const mesh = createCylinderMesh(context, name, radius, length, material);
  mesh.position.copy(start).add(end).multiplyScalar(0.5);
  mesh.quaternion.setFromUnitVectors(Y_AXIS, direction.normalize());
  return mesh;
}

function createRoundedRectShape(width: number, height: number, radius: number): THREE.Shape {
  const halfWidth = width * 0.5;
  const halfHeight = height * 0.5;
  const safeRadius = Math.max(0.001, Math.min(radius, halfWidth, halfHeight));
  const shape = new THREE.Shape();
  shape.moveTo(-halfWidth + safeRadius, -halfHeight);
  shape.lineTo(halfWidth - safeRadius, -halfHeight);
  shape.quadraticCurveTo(halfWidth, -halfHeight, halfWidth, -halfHeight + safeRadius);
  shape.lineTo(halfWidth, halfHeight - safeRadius);
  shape.quadraticCurveTo(halfWidth, halfHeight, halfWidth - safeRadius, halfHeight);
  shape.lineTo(-halfWidth + safeRadius, halfHeight);
  shape.quadraticCurveTo(-halfWidth, halfHeight, -halfWidth, halfHeight - safeRadius);
  shape.lineTo(-halfWidth, -halfHeight + safeRadius);
  shape.quadraticCurveTo(-halfWidth, -halfHeight, -halfWidth + safeRadius, -halfHeight);
  return shape;
}

function createRoundedRectPerimeter(
  width: number,
  height: number,
  radius: number,
  pointsPerCorner: number,
  z: number,
): THREE.Vector3[] {
  const points: THREE.Vector3[] = [];
  const halfWidth = width * 0.5;
  const halfHeight = height * 0.5;
  const centers: ReadonlyArray<readonly [number, number, number]> = [
    [halfWidth - radius, halfHeight - radius, 0],
    [-halfWidth + radius, halfHeight - radius, Math.PI / 2],
    [-halfWidth + radius, -halfHeight + radius, Math.PI],
    [halfWidth - radius, -halfHeight + radius, Math.PI * 1.5],
  ];
  for (const [centerX, centerY, startAngle] of centers) {
    for (let index = 0; index < pointsPerCorner; index += 1) {
      const angle = startAngle + (index / pointsPerCorner) * Math.PI * 0.5;
      points.push(new THREE.Vector3(centerX + Math.cos(angle) * radius, centerY + Math.sin(angle) * radius, z));
    }
  }
  return points;
}

function sampleRoundedRect(
  width: number,
  height: number,
  radius: number,
  count: number,
  z: number,
): THREE.Vector3[] {
  const curve = new THREE.CatmullRomCurve3(createRoundedRectPerimeter(width, height, radius, 8, z), true, 'centripetal');
  return curve.getSpacedPoints(count).slice(0, -1);
}

function createSkylineShape(random: () => number, width: number, maxHeight: number, buildingCount: number): THREE.Shape {
  const shape = new THREE.Shape();
  const left = -width * 0.5;
  const buildingWidth = width / buildingCount;
  const firstHeight = 1.7 + random() * (maxHeight - 1.7);
  shape.moveTo(left, 0);
  shape.lineTo(left, firstHeight);
  let previousHeight = firstHeight;
  for (let index = 0; index < buildingCount; index += 1) {
    const x = left + (index + 1) * buildingWidth;
    shape.lineTo(x, previousHeight);
    if (index < buildingCount - 1) {
      previousHeight = 1.5 + random() * (maxHeight - 1.5);
      shape.lineTo(x, previousHeight);
      if (index % 4 === 2) {
        shape.lineTo(x + buildingWidth * 0.14, previousHeight + 0.6);
        shape.lineTo(x + buildingWidth * 0.28, previousHeight);
      }
    }
  }
  shape.lineTo(width * 0.5, 0);
  shape.closePath();
  return shape;
}

function createCanvas(width: number, height: number): { canvas: HTMLCanvasElement; context: CanvasRenderingContext2D } {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d');
  if (!context) throw new Error('Could not create arcade scene canvas texture context.');
  return { canvas, context };
}

function finalizeCanvasTexture(canvas: HTMLCanvasElement, anisotropy = 4): THREE.CanvasTexture {
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.anisotropy = anisotropy;
  texture.needsUpdate = true;
  return texture;
}

function createFloorTexture(anisotropy: number): THREE.CanvasTexture {
  const { canvas, context } = createCanvas(512, 512);
  context.fillStyle = '#211326';
  context.fillRect(0, 0, 512, 512);
  for (let y = 0; y < 8; y += 1) {
    for (let x = 0; x < 8; x += 1) {
      context.fillStyle = (x + y) % 2 === 0 ? '#2d1830' : '#1b1427';
      context.fillRect(x * 64 + 2, y * 64 + 2, 60, 60);
    }
  }
  context.strokeStyle = 'rgba(255, 192, 78, 0.16)';
  context.lineWidth = 3;
  context.strokeRect(4, 4, 504, 504);
  for (let index = 0; index < 120; index += 1) {
    const x = (index * 137) % 512;
    const y = (index * 73) % 512;
    context.fillStyle = index % 3 === 0 ? 'rgba(255,73,164,.16)' : 'rgba(61,223,235,.11)';
    context.fillRect(x, y, 2 + (index % 3), 2 + (index % 2));
  }
  const texture = finalizeCanvasTexture(canvas, anisotropy);
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  texture.repeat.set(3, 2.4);
  return texture;
}

function createAwningTexture(
  primary: string,
  secondary: string,
  anisotropy: number,
): THREE.CanvasTexture {
  const { canvas, context } = createCanvas(512, 192);
  context.fillStyle = secondary;
  context.fillRect(0, 0, 512, 192);
  for (let x = -192; x < 704; x += 96) {
    context.fillStyle = primary;
    context.beginPath();
    context.moveTo(x, 0);
    context.lineTo(x + 64, 0);
    context.lineTo(x + 192, 192);
    context.lineTo(x + 128, 192);
    context.closePath();
    context.fill();
  }
  context.fillStyle = 'rgba(255,255,255,.13)';
  context.fillRect(0, 0, 512, 8);
  context.fillStyle = 'rgba(0,0,0,.18)';
  context.fillRect(0, 178, 512, 14);
  return finalizeCanvasTexture(canvas, anisotropy);
}

function createBackdropTexture(anisotropy: number): THREE.CanvasTexture {
  const { canvas, context } = createCanvas(1024, 512);
  const gradient = context.createLinearGradient(0, 0, 0, 512);
  gradient.addColorStop(0, '#090515');
  gradient.addColorStop(0.48, '#160727');
  gradient.addColorStop(0.78, '#34133a');
  gradient.addColorStop(1, '#0b0714');
  context.fillStyle = gradient;
  context.fillRect(0, 0, 1024, 512);
  const glow = context.createRadialGradient(512, 360, 10, 512, 360, 390);
  glow.addColorStop(0, 'rgba(255,44,146,.34)');
  glow.addColorStop(0.45, 'rgba(66,220,235,.09)');
  glow.addColorStop(1, 'rgba(0,0,0,0)');
  context.fillStyle = glow;
  context.fillRect(0, 0, 1024, 512);
  for (let index = 0; index < 70; index += 1) {
    const x = (index * 193) % 1024;
    const y = 18 + ((index * 83) % 310);
    const radius = index % 7 === 0 ? 2 : 1;
    context.fillStyle = index % 3 === 0 ? 'rgba(255,191,80,.58)' : 'rgba(255,255,255,.32)';
    context.beginPath();
    context.arc(x, y, radius, 0, Math.PI * 2);
    context.fill();
  }
  return finalizeCanvasTexture(canvas, anisotropy);
}

function createRunnerTexture(): THREE.CanvasTexture {
  const { canvas, context } = createCanvas(512, 768);
  const gradient = context.createLinearGradient(0, 0, 0, 768);
  gradient.addColorStop(0, '#5d123e');
  gradient.addColorStop(0.52, '#971955');
  gradient.addColorStop(1, '#3e1138');
  context.fillStyle = gradient;
  context.fillRect(0, 0, 512, 768);
  context.strokeStyle = '#f3ae3b';
  context.lineWidth = 18;
  context.strokeRect(18, 18, 476, 732);
  context.strokeStyle = '#ff5ba8';
  context.lineWidth = 5;
  context.strokeRect(42, 42, 428, 684);
  context.save();
  context.translate(256, 390);
  context.rotate(-Math.PI / 2);
  context.textAlign = 'center';
  context.textBaseline = 'middle';
  context.font = '900 76px Arial Black, Impact, sans-serif';
  context.fillStyle = '#ffe4a8';
  context.shadowColor = '#ff2c8e';
  context.shadowBlur = 18;
  context.fillText('PLAY THE TIDE', 0, 0);
  context.restore();
  return finalizeCanvasTexture(canvas, 8);
}

function createSignTexture(title: string, subtitle: string, palette: 'pink' | 'cyan'): THREE.CanvasTexture {
  const { canvas, context } = createCanvas(1024, 320);
  const gradient = context.createLinearGradient(0, 0, 1024, 320);
  if (palette === 'pink') {
    gradient.addColorStop(0, '#25091e');
    gradient.addColorStop(0.5, '#6a123f');
    gradient.addColorStop(1, '#1a1027');
  } else {
    gradient.addColorStop(0, '#071b2b');
    gradient.addColorStop(0.5, '#07566a');
    gradient.addColorStop(1, '#15102c');
  }
  context.fillStyle = gradient;
  context.fillRect(0, 0, 1024, 320);
  context.strokeStyle = palette === 'pink' ? '#ff5ba7' : '#52edf2';
  context.lineWidth = 12;
  context.strokeRect(12, 12, 1000, 296);
  context.strokeStyle = '#f7b943';
  context.lineWidth = 4;
  context.strokeRect(30, 30, 964, 260);
  for (const x of [80, 944]) {
    context.fillStyle = '#ffd568';
    context.beginPath();
    for (let point = 0; point < 8; point += 1) {
      const angle = (point / 8) * Math.PI * 2;
      const radius = point % 2 === 0 ? 22 : 8;
      const px = x + Math.cos(angle) * radius;
      const py = 160 + Math.sin(angle) * radius;
      if (point === 0) context.moveTo(px, py);
      else context.lineTo(px, py);
    }
    context.closePath();
    context.fill();
  }
  context.textAlign = 'center';
  context.textBaseline = 'middle';
  context.shadowColor = palette === 'pink' ? '#ff268a' : '#20dce8';
  context.shadowBlur = 20;
  context.fillStyle = '#fff2c5';
  context.font = '900 92px Arial Black, Impact, sans-serif';
  context.fillText(title, 512, 132, 790);
  context.shadowBlur = 8;
  context.fillStyle = '#ffc14e';
  context.font = '700 31px Arial, sans-serif';
  context.letterSpacing = '7px';
  context.fillText(subtitle, 512, 235, 760);
  return finalizeCanvasTexture(canvas, 8);
}

function createBokehTexture(): THREE.CanvasTexture {
  const { canvas, context } = createCanvas(128, 128);
  const gradient = context.createRadialGradient(64, 64, 2, 64, 64, 60);
  gradient.addColorStop(0, 'rgba(255,255,255,1)');
  gradient.addColorStop(0.25, 'rgba(255,255,255,.8)');
  gradient.addColorStop(0.62, 'rgba(255,255,255,.18)');
  gradient.addColorStop(1, 'rgba(255,255,255,0)');
  context.fillStyle = gradient;
  context.fillRect(0, 0, 128, 128);
  return finalizeCanvasTexture(canvas, 2);
}

function createProceduralEnvironmentTexture(): THREE.CanvasTexture {
  const { canvas, context } = createCanvas(1024, 512);
  const gradient = context.createLinearGradient(0, 0, 0, 512);
  gradient.addColorStop(0, '#2b183e');
  gradient.addColorStop(0.47, '#130b22');
  gradient.addColorStop(1, '#07070e');
  context.fillStyle = gradient;
  context.fillRect(0, 0, 1024, 512);
  const cards: ReadonlyArray<readonly [number, number, number, string]> = [
    [180, 255, 230, 'rgba(255,36,142,.75)'],
    [810, 230, 220, 'rgba(38,222,237,.7)'],
    [510, 70, 190, 'rgba(255,192,72,.56)'],
  ];
  for (const [x, y, radius, color] of cards) {
    const glow = context.createRadialGradient(x, y, 0, x, y, radius);
    glow.addColorStop(0, color);
    glow.addColorStop(1, 'rgba(0,0,0,0)');
    context.fillStyle = glow;
    context.fillRect(x - radius, y - radius, radius * 2, radius * 2);
  }
  const texture = finalizeCanvasTexture(canvas, 4);
  texture.mapping = THREE.EquirectangularReflectionMapping;
  return texture;
}

function createArcadeEnvironmentMap(renderer: THREE.WebGLRenderer): THREE.WebGLRenderTarget {
  const pmrem = new THREE.PMREMGenerator(renderer);
  const environmentScene = new THREE.Scene();
  environmentScene.background = new THREE.Color('#090612');
  const geometries: THREE.BufferGeometry[] = [];
  const materials: THREE.Material[] = [];
  const roomGeometry = new THREE.BoxGeometry(24, 16, 24);
  const roomMaterial = new THREE.MeshBasicMaterial({ color: '#10091c', side: THREE.BackSide });
  geometries.push(roomGeometry);
  materials.push(roomMaterial);
  environmentScene.add(new THREE.Mesh(roomGeometry, roomMaterial));
  const cards: ReadonlyArray<readonly [THREE.ColorRepresentation, THREE.Vector3, THREE.Vector2]> = [
    ['#ff3192', new THREE.Vector3(-8, 2.5, 1), new THREE.Vector2(5, 8)],
    ['#31dce8', new THREE.Vector3(8, 2, -1), new THREE.Vector2(4, 7)],
    ['#ffc052', new THREE.Vector3(0, 7, 1), new THREE.Vector2(8, 3)],
  ];
  for (const [color, position, size] of cards) {
    const cardGeometry = new THREE.PlaneGeometry(size.x, size.y);
    const cardMaterial = new THREE.MeshBasicMaterial({ color, side: THREE.DoubleSide });
    geometries.push(cardGeometry);
    materials.push(cardMaterial);
    const card = new THREE.Mesh(cardGeometry, cardMaterial);
    card.position.copy(position);
    card.lookAt(0, 2, 0);
    environmentScene.add(card);
  }
  const target = pmrem.fromScene(environmentScene, 0.035, 0.1, 50);
  pmrem.dispose();
  for (const geometry of geometries) geometry.dispose();
  for (const material of materials) material.dispose();
  return target;
}

function collectDiagnostics(root: THREE.Object3D): ArcadeSceneDiagnostics {
  const geometries = new Set<THREE.BufferGeometry>();
  const materials = new Set<THREE.Material>();
  const textures = new Set<THREE.Texture>();
  let meshes = 0;
  let instancedMeshes = 0;
  let instances = 0;
  let triangles = 0;
  let approximateDrawSurfaces = 0;
  root.traverse((object) => {
    const mesh = object as THREE.Mesh;
    if (!mesh.isMesh) return;
    meshes += 1;
    const instanced = object as THREE.InstancedMesh;
    const multiplier = instanced.isInstancedMesh ? instanced.count : 1;
    if (instanced.isInstancedMesh) {
      instancedMeshes += 1;
      instances += instanced.count;
    }
    geometries.add(mesh.geometry);
    const position = mesh.geometry.getAttribute('position');
    const triangleCount = mesh.geometry.index ? mesh.geometry.index.count / 3 : (position?.count ?? 0) / 3;
    triangles += Math.round(triangleCount * multiplier);
    const meshMaterials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    approximateDrawSurfaces += Math.max(1, meshMaterials.length);
    for (const material of meshMaterials) {
      materials.add(material);
      for (const value of Object.values(material as unknown as Record<string, unknown>)) {
        if (value && typeof value === 'object' && (value as THREE.Texture).isTexture) {
          textures.add(value as THREE.Texture);
        }
      }
    }
  });
  return {
    meshes,
    instancedMeshes,
    instances,
    geometries: geometries.size,
    materials: materials.size,
    textures: textures.size,
    triangles,
    approximateDrawSurfaces,
  };
}
